import type { Game, UseGameStartOptions } from '@game/types'
import { type UseApiOptions, type PlaygameStartGameData, BroadcastMessageType } from '@core/types'
import { useQuery, openPopupWindow, useDevice, useGuard, navigateTo, useUserInfo, shallowRef, type MaybeRefOrGetter, toValue, useApi, useBonusPlayCheck, useCoreConfig, usePlaygameConnectionState, useSharedBroadcast, useState } from '#imports'

export const useGame = () => {
    const { site } = useCoreConfig()
    const { connected: playgameConnected } = usePlaygameConnectionState()
    const { sendMessage } = useSharedBroadcast()

    const api = useApi({ service: 'gameConfiguration' })
    const { isDesktop } = useDevice()
    const userInfo = useUserInfo()
    const { can } = useGuard()
    const { shouldShowBonusModal, showModal } = useBonusPlayCheck()

    const popupWindowReference = useState<Window | null>('gamePopupWindowReference', () => shallowRef(null))

    function getGameBySlug(slug: MaybeRefOrGetter<string>, options: { lazy?: boolean } = {}) {
        const { lazy = false } = options
        const key = () => `game:gameBySlug-${toValue(slug) || 'none'}`
        const enabled = () => !!toValue(slug)

        return useQuery(async () => {
            const endpoint = `/gateway/game-configuration/public/game-configurations/${toValue(slug)}`

            const res = await api<Game>(endpoint, {
                query: {
                    brand: site.brand
                }
            })

            return res
        }, {
            key,
            enabled,
            lazy,
            server: !lazy,
        })
    }

    function getGameBySeoName(seoName: MaybeRefOrGetter<string>, options: { lazy?: boolean } = {}) {
        const { lazy = false } = options
        const key = () => `game:gameBySeoName-${toValue(seoName) || 'none'}`
        const enabled = () => !!toValue(seoName)

        return useQuery(async () => {
            const endpoint = '/gateway/game-configuration/public/game-configurations/search'

            const queryOptions: UseApiOptions = {
                method: 'POST',
                body: {
                    filter: {
                        seo_name: toValue(seoName),
                    },
                    brand: site.brand,
                },
            }

            const res = await api(endpoint, queryOptions)

            if (res.data.length) {
                return res.data[0]
            }

            return res
        }, {
            key,
            enabled,
            lazy,
            server: !lazy,
        })
    }

    const getPlaygameUrl = () => {
        return '/playgame'
    }

    const getGameUrl = () => {
        const url = getPlaygameUrl()

        return url
    }

    const getGameParams = (options: UseGameStartOptions): PlaygameStartGameData => {
        const params: PlaygameStartGameData = {
            start: options.gameSlug,
            brand: site.brand,
        }

        if (options.ticketsReceived) {
            params.tickets = options.ticketsReceived.toString()
        }

        if (options.gameInstanceId) {
            params.gameInstanceId = options.gameInstanceId
        }

        if (options.providerSlug) {
            params.provider = options.providerSlug
        }

        return params
    }

    const handleNewPlaygameBroadcast = (params: PlaygameStartGameData): boolean => {
        if (!playgameConnected.value) return false
        if (!popupWindowReference.value || popupWindowReference.value.closed) return false

        sendMessage({
            type: BroadcastMessageType.PLAYGAME_START_GAME,
            data: params,
        })

        popupWindowReference.value.focus()

        return true
    }

    const start = async (options: UseGameStartOptions) => {
        const { gameSlug, gameInstanceId = null, redirect = false, isPlaygame } = options

        const isAllowed = await can()

        if (!isAllowed) return

        if (userInfo.isLoggedIn.value && !options.bypassBonusCheck && shouldShowBonusModal(options.game, options.isPlaygame)) {
            showModal(options)
            sendMessage({ type: BroadcastMessageType.GAME_TILE_STOP_LOADING })
            return
        }

        document.dispatchEvent(new CustomEvent('gameStarted', {
            detail: gameSlug,
        }))

        const shouldUsePopup = !isPlaygame && isDesktop && !redirect

        if (!userInfo.isLoggedIn.value) {
            const path = `/games/entrance/${gameSlug}`

            if (shouldUsePopup) {
                popupWindowReference.value = openPopupWindow(path, 'play')

                return
            }

            await navigateTo({ path })

            return
        }

        const url = getGameUrl(gameSlug, gameInstanceId)
        const queryParams = getGameParams(options)

        // if the window was closed, null the reference so a new popup opens instead of messaging a dead window
        if (popupWindowReference.value?.closed) {
            popupWindowReference.value = null
        }

        // If we are using the new detached playgame logic and a session is active
        if (shouldUsePopup && handleNewPlaygameBroadcast(queryParams)) {
            return
        }

        if (shouldUsePopup) {
            const urlWithParams = `${url}?${new URLSearchParams(queryParams as any).toString()}`

            popupWindowReference.value = openPopupWindow(urlWithParams, 'play')

            return
        }

        await navigateTo({
            path: url,
            query: queryParams,
        })
    }

    return userInfo.bind({
        getPlaygameUrl,
        getGameUrl,
        getGameBySlug,
        getGameBySeoName,
        getGameParams,
        start,
    })
}
